import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { IntercomProps, useIntercom } from 'react-use-intercom';
import { AuthenticationStatus, QwaSession, QwaUser } from '../../../auth/types';
import { isExpressCustomer } from '../../../utility/services/authActions';
import { extractUTMFromQueryParams, UTMInfo } from '../../query';
import { TRIAL_SCHEMA } from '../../../config/auth';
import { useSession, useIsTestDriveUser, useSchema } from './auth';
import { CONTENT_AI_USER_TYPE } from '../../../constants/common';
import { dispatchCustomerInfoQuery } from '../api/common';
import { useDispatch } from 'react-redux';
import { USER_CLASSIFICATION } from '../../common';

export function useWrappedText(text = '', limit = 200) {
  const trimmedText = typeof text === 'string' ? text.substr(0, limit) : '';

  const [displayText, setText] = useState(
    trimmedText.substr(0, Math.min(trimmedText.length, trimmedText.lastIndexOf(' ')))
  );
  const [isWrapped, setWrapped] = useState(true);
  const toggleTextWrapping = () => {
    if (typeof text === 'string') {
      setText(isWrapped ? text : trimmedText.substr(0, Math.min(trimmedText.length, trimmedText.lastIndexOf(' '))));
      setWrapped(!isWrapped);
    }
  };
  return [displayText, isWrapped, toggleTextWrapping];
}

// Hook
export function useKeyPress(targetKey) {
  // State for keeping track of whether key is pressed
  const [keyPressed, setKeyPressed] = useState(false);

  // If pressed key is our target key then set to true
  function downHandler({ key }) {
    if (key === targetKey) {
      setKeyPressed(true);
    }
  }

  // If released key is our target key then set to false
  const upHandler = ({ key }) => {
    if (key === targetKey) {
      setKeyPressed(false);
    }
  };

  // Add event listeners
  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return keyPressed;
}

export function useOnClickOutside(ref, handler) {
  useEffect(
    () => {
      const listener = (event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);

      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
}

/**
 * Sets the user info for Intercom if available
 */
export const useIntercomUserInfo = () => {
  const { update } = useIntercom();
  const router = useRouter();
  const dispatch = useDispatch();
  const [isExpressData, setIsExpressData] = useState<boolean>();
  const { session, authState } = useSession();
  const authenticated = authState === AuthenticationStatus.Authenticated;
  const qwaSession = session as QwaSession;
  const isTestDriveUser = useIsTestDriveUser();

  // TODO no longer used after okta upgrade
  // delete after okta upgrade has been in prod for at least 1 month
  // const getSubscriberEmailOrInternalUserName = (email: string): string => {
  //   if (email.endsWith('@quattr.com')) {
  //     return email.slice(0, email.indexOf('@'));
  //   } else {
  //     return email;
  //   }
  // };

  useEffect(() => {
    const updateData = async () => {
      if (authenticated && qwaSession?.user?.email) {
        const utmInfo = () => extractUTMFromQueryParams(router.query);
        if (isExpressData === null || isExpressData === undefined) {
          const customerInfo = dispatchCustomerInfoQuery(dispatch);
          const res = await isExpressCustomer(session, customerInfo);
          setIsExpressData(Boolean(res));
        }
        // initializing intercom
        // updating here because it needs auth info

        let intercomUserInfo: IntercomProps & UTMInfo = {
          ...utmInfo,
        };

        // const qwaSession = session as QwaSession;
        const qwaUser = qwaSession.user as QwaUser;
        const { name, email, userType } = qwaUser;

        intercomUserInfo = {
          ...intercomUserInfo,
          email: email || undefined,
          name: name || undefined,
          customAttributes: {
            is_test_drive: isTestDriveUser,
            is_express_custromer: isExpressData,
            is_content_ai: userType === CONTENT_AI_USER_TYPE,
            is_tools_user: userType === USER_CLASSIFICATION.QWA_TOOLS,
          },
        };
        update(intercomUserInfo);
      }
    };

    updateData();
  }, [authenticated, isExpressData, isTestDriveUser, qwaSession, router.query, session, update]);
};

export type UseDialogInfoFromUrlResult = {
  isTestDriveOpen: boolean;
  isRequestDemoOpen: boolean;
};

/**
 * Centralized logic to determine which dialogs/modals should be open by default
 *
 * Prevents an edge case where both can be told to be open at the same time,
 * giving priority to Test Drives
 */
export const useDialogInfoFromUrl = (): UseDialogInfoFromUrlResult => {
  const { query } = useRouter();

  const result = {
    isTestDriveOpen: false,
    isRequestDemoOpen: false,
  };

  if (query.testDrive !== undefined) {
    result.isTestDriveOpen = true;
  }

  if (!result.isTestDriveOpen && query.requestDemo !== undefined) {
    result.isRequestDemoOpen = true;
  }

  return result;
};

/**
 *
 * @returns `undefined` is the session is not loaded yet, `boolean` otherwise
 */
export const useIsTrial = () => {
  const schema = useSchema();

  // be more specific in case the downstream caller wants to know if session is still loading
  if (schema === undefined) {
    return undefined;
  }

  if (schema === TRIAL_SCHEMA) {
    return true;
  }

  return false;
};

export * from './hubSpot';
export * from './prefetchData';
export * from './snackbar';
export * from './window';
export * from './auth';
